import React, { memo, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { DecorItemI } from "../../../../shared";
import DecorWallDimensions from "./DecorWallDimensions";
import DecorFloorDimensions from "./DecorFloorDimensions";
import { ConstructionsPrimitive, FBXPrimitive, GLBPrimitive, TriangularPrismPrimitive } from "./primitives";
import { DecorDraggable } from "./DecorDraggable";
import { useStoreRoom, useStoreSystem } from "../../../../store";
import { Axis, DecorObjectProps } from "../types";
import shallow from "zustand/shallow";
import { BoxBufferGeometry, BoxHelper, Group, Mesh, MeshStandardMaterial } from "three";
import { useFrame, useThree } from "@react-three/fiber";
import { calcCollisions } from "./calcCollisions";
import { createBboxMesh, getGroupSize } from "./createBboxMesh";
import { BBoxItem, getDecorBboxCollection } from "./getDecorBboxCollection";
import { createWireframe } from "./createWireframe";
import { getScaleWithMargin } from "./getScaleWithMargin";
import { useCurrent } from "../../../../hooks/useCurrent";
import { SceneContext } from "../../../Context/SceneContext";
import axios from "axios";
import { UniversalDraggable } from "../../UniversalDraggable";
import { isTriangularPrism } from "../../../../utils/createTriangularPrism";
import {
    createOrientedFootprintTemplate,
    isBicycleDecor,
    OrientedFootprintTemplate,
} from "./orientedFootprint";
import { requiresPreciseWorkbenchToolboxBounds } from "../../../../utils/workbenchToolbox";

const setCursor = (cursor: CSSStyleDeclaration["cursor"]) => {
    document.body.style.cursor = cursor;
};

const getSign = (side: DecorItemI["side"]) => {
    switch (side) {
        case "back":
            return -1;
        case "front":
            return 1;
        case "left":
            return 1;
        case "right":
            return -1;
        default:
            return 1;
    }
};

const calcXOffset = (decor: DecorItemI) => {
    if (decor.surface === "wall" && (decor.side === "left" || decor.side === "right")) {
        return -decor.translate[0] + (getSign(decor.side) * (decor.surfaceOffset ?? 0)) / 1000;
    }
    return -decor.translate[0];
};

const calcYOffset = (decor: DecorItemI) => {
    if (["floor", "ceiling"].includes(decor.surface)) {
        return -decor.translate[1];
    }
    return -decor.translate[1];
};

const calcZOffset = (decor: DecorItemI) => {
    if (["floor", "ceiling"].includes(decor.surface)) {
        return -decor.translate[2];
    }
    return ["left", "right"].includes(decor.side)
        ? -decor.translate[2]
        : decor.translate[2] + (getSign(decor.side) * (decor.surfaceOffset ?? 0)) / 1000;
};

const calcOffsets = (decor: DecorItemI) => {
    const xOffset = calcXOffset(decor);
    const yOffset = calcYOffset(decor);
    const zOffset = calcZOffset(decor);
    return { xOffset, yOffset, zOffset };
};

const DecorObject = (props: DecorObjectProps) => {
    const { decor, moveCameraEnabled } = props;
    const isGLB = /\.(gltf|glb)$/i.test(decor.model);
    const isBicycle = isBicycleDecor(decor);
    const usePreciseBounds = requiresPreciseWorkbenchToolboxBounds(decor);

    const groupRef = useRef<Group | null>(null);
    const decorDraggableRef = useRef<Group | null>(null);
    const bBoxRef = useRef<Mesh<BoxBufferGeometry, MeshStandardMaterial> | null>(null);
    const decorBboxCollectionRef = useRef<BBoxItem[]>();
    const wireframeRef = useRef<Group | null>(null);
    const boxHelperRef = useRef<BoxHelper | null>(null);
    const bicycleFootprintTemplateRef = useRef<OrientedFootprintTemplate | null>(null);
    const { currentEditItem, setCurrentEditItem } = useCurrent();
    const activeObject = useContext(SceneContext);

    const { scene } = useThree();

    const roomLength = useStoreRoom.getState().length / 1000;
    const roomHeight = useStoreRoom.getState().height / 1000;
    const roomDepth = useStoreRoom.getState().depth / 1000;
    const currentSide = useStoreSystem.getState().currentSide;

    const [isEmptyAabb, setIsEmptyAabb] = useState(true);

    const [visible, setVisible] = useState(false);
    const updateDecor = useStoreRoom((state) => state.updateDecor, shallow);

    const { xOffset, yOffset, zOffset } = calcOffsets(decor);

    const position: DecorItemI["position"] = useMemo(
        () => [decor.position[0] + xOffset, decor.position[1] + yOffset, decor.position[2] + zOffset],
        [decor.position, xOffset, yOffset, zOffset]
    );

    const computedAvailableAxis: Axis[] = ["floor", "ceiling"].includes(decor.surface)
        ? ["z", "x"]
        : ["front", "back"].includes(decor.side)
        ? ["x", "y"]
        : ["z", "y"];

    const handleClick = () => {};

    const handleDragStart = async () => {
        moveCameraEnabled.current = false;
        setCursor("grabbing");
        if (groupRef.current) {
            decorBboxCollectionRef.current = getDecorBboxCollection(scene, groupRef.current);

            if (isBicycle) {
                bicycleFootprintTemplateRef.current = createOrientedFootprintTemplate(
                    groupRef.current,
                    decor.margin
                );
            }

            if (!bBoxRef.current) {
                const clonedObj = groupRef.current.clone();
                const scale = getScaleWithMargin(clonedObj, decor);
                clonedObj.scale.set(...scale);
                bBoxRef.current = createBboxMesh(clonedObj, usePreciseBounds);
                const bBoxYPos =
                    decor.position[1] +
                    (["floor", "ceiling"].includes(decor.surface) && decor.surfaceOffset
                        ? decor.surfaceOffset / 1000
                        : 0);
                bBoxRef.current.position.set(position[0], bBoxYPos, position[2]);
                boxHelperRef.current = new BoxHelper(bBoxRef.current, 0xffff00);
                scene.add(boxHelperRef.current);
                scene.add(bBoxRef.current);
            }
        }
    };

    const handleDrag = (position: DecorItemI["position"]) => {
        setCursor("grabbing");
        if (!visible) {
            setVisible(true);
        }

        if (bBoxRef.current && decorBboxCollectionRef.current) {
            const correctedPosition = calcCollisions({
                currentObject: bBoxRef.current,
                decor,
                position,
                decorBboxCollection: decorBboxCollectionRef.current,
                roomSizes: { roomLength, roomHeight, roomDepth },
                offset: { x: xOffset, y: yOffset, z: zOffset },
                currentFootprintTemplate: bicycleFootprintTemplateRef.current ?? undefined,
            });

            if (correctedPosition) {
                updateDecor({
                    ...decor,
                    position: correctedPosition,
                });
            }
        }
    };

    useEffect(() => {
        return () => {
            clearHelpers();
        };
    }, []);

    useEffect(() => {
        if (bBoxRef.current) {
            const bBoxYPos =
                decor.position[1] +
                (["floor", "ceiling"].includes(decor.surface) && decor.surfaceOffset ? decor.surfaceOffset / 1000 : 0);
            bBoxRef.current.position.set(position[0], bBoxYPos, position[2]);
        }
        if (boxHelperRef.current) {
            boxHelperRef.current.update();
        }
    }, [decor.position, position]);

    const clearHelpers = () => {
        bicycleFootprintTemplateRef.current = null;

        if (bBoxRef.current) {
            scene.remove(bBoxRef.current);
            bBoxRef.current = null;
        }
        if (wireframeRef.current) {
            scene.remove(wireframeRef.current);
            wireframeRef.current = null;
        }
        if (boxHelperRef.current) {
            scene.remove(boxHelperRef.current);
            boxHelperRef.current = null;
        }
    };

    const handleDragEnd = () => {
        moveCameraEnabled.current = true;
        setVisible(false);
        setCursor("grab");
        clearHelpers();
        setCurrentEditItem(decor.id, false);
    };

    const handleEdit = () => {
        if (currentEditItem.id) {
            if (activeObject) {
                activeObject.current = null;
            }
        }
        clearHelpers();
        moveCameraEnabled.current = true;
        setVisible(false);
        setCurrentEditItem(decor.id, false);
    };

    const handlePointerEnter = useCallback(() => {
        if (decor.surface === "wall" && decor.side !== currentSide) {
            return;
        }
        setCursor("grab");
    }, [setCursor]);

    const handlePointerOut = useCallback(() => {
        if (decor.surface === "wall" && decor.side !== currentSide) {
            return;
        }
        setCursor("default");
    }, [setCursor]);

    const [isAvailable3dModel, setIsAvailable3dModel] = useState(false);

    const updateFloorDecorPosition = () => {
        if (groupRef.current && ["floor", "ceiling"].includes(decor.surface)) {
            decorBboxCollectionRef.current = getDecorBboxCollection(scene, groupRef.current);
            const { size } = getGroupSize(groupRef.current, usePreciseBounds);
            if (size.length() === 0) {
                return;
            }
            const correctedPosition = calcCollisions({
                currentObject: groupRef.current,
                decor,
                position: decor.position,
                decorBboxCollection: decorBboxCollectionRef.current,
                roomSizes: { roomLength, roomHeight, roomDepth },
                offset: { x: xOffset, y: yOffset, z: zOffset },
            });

            if (correctedPosition) {
                updateDecor({
                    ...decor,
                    position: [correctedPosition[0], size.y / 2 + (decor.margin ?? 0) / 1000, correctedPosition[2]],
                });
            }
        }
    };

    useEffect(() => {
        updateFloorDecorPosition();
    }, [decor.rotation, roomDepth, roomLength]);

    useFrame(() => {
        if (isEmptyAabb && groupRef.current && ["floor", "ceiling"].includes(decor.surface)) {
            const { aabb } = getGroupSize(groupRef.current);
            const isEmpty = aabb.isEmpty();

            if (!isEmpty) {
                updateFloorDecorPosition();
                setIsEmptyAabb(false);
            }
        }
    });

    useLayoutEffect(() => {
        axios
            .get(decor.model)
            .then(() => {
                setIsAvailable3dModel(true);
            })
            .catch(() => {
                console.error(`В БД отсутствует 3d модель для "${decor.name}"`);
            });
    }, []);

    return (
        <group name={`${decor.side}Decor${decor.surface === "wall" ? "" : decor.surface}`}>
            <UniversalDraggable
                decorRef={decorDraggableRef}
                position={decor.position}
                onClick={handleClick}
                onDrag={handleDrag}
                onDragEnd={handleDragEnd}
                onDragStart={handleDragStart}
                onEdit={handleEdit}
                availableAxis={computedAvailableAxis}
                side={decor.side}
                isWallDecor={decor.surface === "wall"}
            >
                <group
                    ref={groupRef}
                    rotation={[decor.rotation.x, decor.rotation.y, decor.rotation.z]}
                    position={[
                        position[0],
                        position[1] -
                            yOffset +
                            (["floor", "ceiling"].includes(decor.surface) && decor.surfaceOffset
                                ? decor.surfaceOffset / 1000
                                : 0),
                        position[2],
                    ]}
                    name={`decor-${decor.id}`}
                    onPointerOut={handlePointerOut}
                    onPointerEnter={handlePointerEnter}
                >
                    <group position={[0, yOffset, 0]}>
                        {isAvailable3dModel && (
                            <>
                                {isGLB ? (
                                    <GLBPrimitive visible={isBicycle || !visible} decor={decor} gltfRoom={props.gltfRoom} />
                                ) : (
                                    <FBXPrimitive visible={isBicycle || !visible} decor={decor} gltfRoom={props.gltfRoom} />
                                )}
                            </>
                        )}
                        {!isAvailable3dModel && decor.type === "constructions" && !isTriangularPrism(decor) && (
                            <ConstructionsPrimitive visible={!visible} decor={decor} gltfRoom={props.gltfRoom} />
                        )}
                        {!isAvailable3dModel && isTriangularPrism(decor) && (
                            <TriangularPrismPrimitive visible={!visible} decor={decor} gltfRoom={props.gltfRoom} />
                        )}
                    </group>
                </group>
            </UniversalDraggable>
            <DecorWallDimensions decor={decor} visible={decor.surface === "wall" && visible} />
            {["floor", "ceiling"].includes(decor.surface) && visible && (
                <DecorFloorDimensions bBoxDecor={bBoxRef.current ? bBoxRef.current : undefined} decor={decor} />
            )}
        </group>
    );
};

export default DecorObject;
